import type { AreaFrameUpdateData } from "@situate/contracts";
import type { AreaMapResponse } from "@/features/deployment-dashboard/shared/api/area-map-response";
import {
  calculatePolygonArea,
  isPointInsidePolygon,
  type PolygonVertex,
} from "@/features/deployment-dashboard/live-view/utils/polygon-geometry";

export interface LiveViewObservationAreaStats {
  id: string;
  name: string;
  population: number;
  areaSquareMeters: number;
  peoplePerSquareMeter: number;
}

export interface LiveViewStats {
  population: number;
  averageDensity: number | null;
  observationAreas: LiveViewObservationAreaStats[];
}

function toPolygonVertices(
  vertices: Array<{ eastingM: number; northingM: number }>
): PolygonVertex[] {
  return vertices.map((vertex) => ({ x: vertex.eastingM, y: vertex.northingM }));
}

/**
 * Derives the legacy live-map sidebar metrics from current runtime frames and
 * configured observation-area polygons. Polygon coordinates are metres, so the
 * resulting areas and densities retain their physical units.
 */
export function deriveLiveViewStats(
  latestAreaFrames: Record<string, AreaFrameUpdateData>,
  areaMaps: AreaMapResponse[]
): LiveViewStats {
  const population = Object.values(latestAreaFrames).reduce(
    (total, frame) => total + frame.objects.length,
    0
  );

  const observationAreas = areaMaps.flatMap((areaMap) => {
    const objects = latestAreaFrames[areaMap.areaId]?.objects ?? [];

    return areaMap.observationAreas.map((observationArea) => {
      const vertices = toPolygonVertices(observationArea.polygon.vertices);
      const areaSquareMeters = calculatePolygonArea(vertices);
      const areaPopulation = objects.filter((object) =>
        isPointInsidePolygon(object.position, vertices)
      ).length;

      return {
        id: `${areaMap.areaId}:${observationArea.observationAreaId}`,
        name: observationArea.observationAreaName,
        population: areaPopulation,
        areaSquareMeters,
        peoplePerSquareMeter: areaSquareMeters > 0 ? areaPopulation / areaSquareMeters : 0,
      };
    });
  });

  const averageDensity =
    observationAreas.length > 0
      ? observationAreas.reduce((total, area) => total + area.peoplePerSquareMeter, 0) /
        observationAreas.length
      : null;

  return { population, averageDensity, observationAreas };
}
